/* eslint-disable @typescript-eslint/no-explicit-any */
import { Canvas, Object as FabricObject, Rect, Textbox, Image } from 'fabric';

import { type Layer } from '../types/types';

import { useCanvasStore } from '../store';

// Минимальные размеры, если в objData нет width/height
const DEFAULT_PLACEHOLDER = { width: 200, height: 150 };

export function createPlaceholderForImage(objData: any): FabricObject {
  const width = objData.width || DEFAULT_PLACEHOLDER.width;
  const height = objData.height || DEFAULT_PLACEHOLDER.height;
  const placeholder = new Rect({
    left: objData.left ?? 0,
    top: objData.top ?? 0,
    width,
    height,
    fill: '#EFF1F3',
    stroke: '#D2D6DB',
    strokeDashArray: [6, 4],
    angle: objData.angle ?? 0,
    originX: objData.originX || 'left',
    originY: objData.originY || 'top',
    opacity: 1,
    evented: false,
    selectable: false,
  });

  // Устанавливаем кастомные поля
  placeholder.id = objData.id;
  placeholder.zIndex = objData.zIndex || 0;
  placeholder.category = objData.category || 'content';
  placeholder.layer = objData.layer || 'background';

  // 🔗 Сохраняем markerId для объектов контента маркеров
  if (objData.markerId) {
    (placeholder as any).markerId = objData.markerId;
    (placeholder as any).isMarkerContent = true;
    console.debug('🔗 Placeholder created with markerId:', {
      placeholderId: placeholder.id,
      markerId: objData.markerId,
    });
  }

  // Текст «Loading…» с учетом origin плейсхолдера
  let labelLeft = objData.left ?? 0;
  let labelTop = objData.top ?? 0;

  // Корректируем позицию лейбла в зависимости от origin
  if (objData.originX === 'center') {
    labelLeft = labelLeft - 40; // половина ширины текста (~80px)
  } else if (objData.originX === 'right') {
    labelLeft = labelLeft - width - 40;
  } else {
    // left origin
    labelLeft = labelLeft + width / 2 - 40;
  }

  if (objData.originY === 'center') {
    labelTop = labelTop - 10; // половина высоты текста (~20px)
  } else if (objData.originY === 'bottom') {
    labelTop = labelTop - height - 10;
  } else {
    // top origin
    labelTop = labelTop + height / 2 - 10;
  }

  const label = new Textbox('Loading…', {
    left: labelLeft,
    top: labelTop,
    fontSize: 14,
    fill: '#6B7280',
    selectable: false,
    evented: false,
    editable: false,
  });

  // Устанавливаем кастомные поля для лейбла
  label.isPlaceholderLabel = true;
  label.forObjectId = objData.id;
  placeholder.__placeholderLabel = label;

  return placeholder;
}

export async function removePlaceholderOnError(
  canvas: Canvas,
  canvasId: string,
  layer: Layer,
  placeholder: FabricObject,
  isNeedMutateStore = true,
): Promise<void> {
  try {
    console.debug('❌ [removePlaceholderOnError] Removing failed placeholder:', {
      canvasId,
      layer,
      placeholderId: placeholder.id,
    });

    // удалить плейсхолдер и подпись
    const placeholderLabel = placeholder.__placeholderLabel;
    canvas.remove(placeholder);
    if (placeholderLabel) canvas.remove(placeholderLabel);

    // обновить store слоёв: удалить плейсхолдер
    if (isNeedMutateStore) {
      const { removeLayerObject } = useCanvasStore.getState();
      removeLayerObject(canvasId, layer, {
        object: placeholder,
        zIndex: placeholder.zIndex || 0,
        layer: placeholder.layer || layer,
      });
    }

    canvas.requestRenderAll();
    console.debug('❌ [removePlaceholderOnError] Failed placeholder removed:', placeholder.id);
  } catch (e) {
    console.error('removePlaceholderOnError error', e);
  }
}

export async function replacePlaceholderWithImage(
  canvas: Canvas,
  canvasId: string,
  layer: Layer,
  placeholder: FabricObject,
  realImage: Image,
  isNeedMutateStore = true,
): Promise<void> {
  try {
    // сохранить геометрию и id
    realImage.set({
      /* TODO: Temporary disabled */
      // left: placeholder.left,
      // top: placeholder.top,
      // angle: placeholder.angle || 0,
    });

    // Устанавливаем кастомные поля
    realImage.id = placeholder.id;
    realImage.zIndex = placeholder.zIndex || 0;
    realImage.layer = layer;

    // 🔗 Переносим markerId с плейсхолдера на реальную картинку
    if ((placeholder as any).markerId) {
      (realImage as any).markerId = (placeholder as any).markerId;
      (realImage as any).isMarkerContent = true;
      console.debug('🔗 Transferred markerId to real image:', {
        imageId: realImage.id,
        markerId: (placeholder as any).markerId,
      });
    }

    // 📍 КРИТИЧНО: Копируем позицию от плейсхолдера!
    // Плейсхолдер уже был синхронизирован после рекалькуляции метабуллитов,
    // поэтому его координаты актуальные, а координаты realImage - устаревшие от бэкенда
    // НЕ копируем размеры - пусть scaleImage сама установит правильные размеры
    const placeholderLeft = placeholder.left || 0;
    const placeholderTop = placeholder.top || 0;

    realImage.set({
      left: placeholderLeft,
      top: placeholderTop,
      angle: placeholder.angle || 0,
    });

    // 📍 Копируем сохраненные оффсеты от плейсхолдера (если есть)
    // Эти оффсеты были вычислены при добавлении placeholder в маркер
    if (placeholder._markerOffsetX !== undefined) {
      realImage._markerOffsetX = placeholder._markerOffsetX;
      // ВАЖНО: offsetY всегда 0 - контент всегда на одной вертикали с центром маркера
      realImage._markerOffsetY = 0;
      realImage._markerOriginX = placeholder._markerOriginX;
      realImage._markerOriginY = placeholder._markerOriginY;
      console.debug('📍 Copied marker offsets from placeholder to real image:', {
        imageId: realImage.id,
        offsetX: realImage._markerOffsetX,
        offsetY: realImage._markerOffsetY,
      });
    }

    console.debug('📍 Copied position from placeholder to real image:', {
      imageId: realImage.id,
      placeholderPosition: { left: placeholderLeft, top: placeholderTop },
      imagePosition: { left: realImage.left, top: realImage.top },
    });

    // Получаем label заранее для использования в нескольких местах
    const label = placeholder.__placeholderLabel;

    // Найдем позицию placeholder'а ПЕРЕД его удалением
    const allObjects = canvas.getObjects();
    const placeholderIndex = allObjects.findIndex((obj) => obj === placeholder);

    // Удаляем placeholder и его label
    canvas.remove(placeholder);
    if (label) canvas.remove(label);

    // Вставляем изображение на то же место где был placeholder
    let insertIndex = placeholderIndex;
    if (insertIndex === -1) {
      // Fallback: если не нашли placeholder, вставляем в конец
      insertIndex = canvas.getObjects().length;
    }

    // Вставляем в правильную позицию
    canvas.insertAt(insertIndex, realImage);

    // 🔗 Если это объект контента маркера - связываем с маркером
    if ((realImage as any).markerId) {
      const markerId = (realImage as any).markerId;

      console.debug('🔍 Searching for marker to link progressive image:', {
        imageId: realImage.id,
        markerId: markerId,
        imageHasMarkerId: !!(realImage as any).markerId,
      });

      // Ищем маркер на канвасе
      const allCanvasObjects = canvas.getObjects();
      const marker = allCanvasObjects.find(
        (obj: any) => obj.type === 'marker' && obj.id === markerId,
      );

      console.debug('🔍 Marker search result:', {
        markerFound: !!marker,
        markerId: marker?.id,
        markerType: marker?.type,
        hasAddContentObjectMethod: marker && typeof (marker as any).addContentObject === 'function',
        totalCanvasObjects: allCanvasObjects.length,
        allMarkers: allCanvasObjects
          .filter((obj: any) => obj.type === 'marker')
          .map((m: any) => ({ id: m.id, entityId: (m as any).entityId })),
      });

      if (marker && (marker as any).addContentObject) {
        console.debug('🔗 Linking progressive image with marker:', {
          imageId: realImage.id,
          markerId: markerId,
          markerContentObjectsBefore: (marker as any).getContentObjectsCount?.() || 0,
        });

        // Удаляем старый плейсхолдер из маркера (если он там был)
        if ((marker as any).removeContentObject && (marker as any).getContentObjects) {
          const existingContents = (marker as any).getContentObjects();
          const placeholderInMarker = existingContents.find(
            (obj: any) => obj.id === placeholder.id,
          );

          if (placeholderInMarker) {
            const removed = (marker as any).removeContentObject(placeholderInMarker);
            console.debug('🗑️ Removed placeholder from marker:', {
              placeholderId: placeholder.id,
              markerId: markerId,
              removed: removed,
            });
          } else {
            console.debug('ℹ️ Placeholder not in marker content (this is OK):', {
              placeholderId: placeholder.id,
              markerId: markerId,
              existingContentIds: existingContents.map((obj: any) => obj.id),
            });
          }
        }

        // Добавляем реальную картинку в маркер
        (marker as any).addContentObject(realImage);

        console.debug('✅ Progressive image linked with marker:', {
          imageId: realImage.id,
          markerId: markerId,
          markerPosition: { left: marker.left, top: marker.top },
          imagePosition: { left: realImage.left, top: realImage.top },
          markerContentObjectsAfter: (marker as any).getContentObjectsCount?.() || 0,
        });
      } else {
        console.error('❌ Marker not found or missing addContentObject method:', {
          imageId: realImage.id,
          markerId: markerId,
          markerFound: !!marker,
          markerType: marker?.type,
          hasAddContentObjectMethod:
            marker && typeof (marker as any).addContentObject === 'function',
          availableMarkers: allCanvasObjects
            .filter((obj: any) => obj.type === 'marker')
            .map((m: any) => ({ id: m.id, entityId: (m as any).entityId, type: m.type })),
        });
      }
    }

    // обновить store слоёв: удалить старый, добавить новый
    if (isNeedMutateStore) {
      const { removeLayerObject, addLayerObject } = useCanvasStore.getState();
      removeLayerObject(canvasId, layer, {
        object: realImage,
        zIndex: realImage.zIndex || 0,
        layer: realImage.layer || layer,
      });
      addLayerObject(canvasId, layer, {
        object: realImage,
        zIndex: realImage.zIndex || 0,
        layer: layer,
      });
    }

    canvas.requestRenderAll();
    console.debug('🎨 [replacePlaceholderWithImage] Scheduling render for:', realImage.id);
    console.debug('✅ [replacePlaceholderWithImage] Replacement completed:', realImage.id);
  } catch (e) {
    console.error('replacePlaceholderWithImage error', e);
  }
}
